﻿namespace Illy.SafeStack.Client

open System

[<RequireQualifiedAccess>]
type ParsedInt = 
    | Empty
    | Valid of int
    | BelowMin of string
    | AboveMax of string
    | Invalid of string

module ParsedInt =
    open System
    open System.Text.RegularExpressions

    let minInt = -999999999
    let maxInt = 999999999
    let regex = Regex("""^-?\s?(\d{1,9})$""", RegexOptions.IgnoreCase)

    let formatError =
        function
        | ParsedInt.BelowMin _ -> "Minimum number allowed is -999999999."
        | ParsedInt.AboveMax _ -> "Maximum number allowed is 999999999."
        | ParsedInt.Invalid str -> sprintf "\"%s\" is not valid (please choose a whole number)." str
        | _ -> ""

    let toOption =
        function
        | ParsedInt.Valid d -> Some d
        | _ -> None

    let fromOption =
        function
        | None -> ParsedInt.Empty
        | Some x -> ParsedInt.Valid x

    let private matchInt input =
        let res = regex.Match(input)

        if not res.Success then
            ParsedInt.Invalid input
        else
            match Int32.TryParse input with
            | true, x when x < minInt -> ParsedInt.BelowMin input
            | true, x when x > maxInt -> ParsedInt.AboveMax input
            | true, x -> ParsedInt.Valid x
            | false,_ -> ParsedInt.Invalid input

    let ofString str =
        if String.IsNullOrWhiteSpace str then 
            (ParsedInt.Empty, None)
        else
            match matchInt str with
            | ParsedInt.Empty as x -> x, None
            | ParsedInt.Valid _ as x -> x, None
            | ParsedInt.BelowMin _ as x -> x, formatError x |> Some 
            | ParsedInt.AboveMax _ as x -> x, formatError x |> Some 
            | ParsedInt.Invalid _ as x -> x, formatError x |> Some 

    let toString formatter = 
        function
        | ParsedInt.Empty -> ""
        | ParsedInt.Valid d -> formatter d
        | ParsedInt.Invalid str
        | ParsedInt.BelowMin str
        | ParsedInt.AboveMax str -> str

    let formatToOption formatter = 
        function
        | ParsedInt.Empty -> None
        | ParsedInt.Valid d -> Some <| formatter d
        | ParsedInt.Invalid str 
        | ParsedInt.BelowMin str
        | ParsedInt.AboveMax str -> Some str

[<RequireQualifiedAccess>]
type ParsedDecimal = 
    | Empty
    | Valid of decimal
    | BelowMin of string
    | AboveMax of string
    | Invalid of string

module ParsedDecimal =
    open System
    open System.Text.RegularExpressions

    let minDec = -999999999M
    let maxDec = 999999999M
    let regex = Regex("""^-?\s?(\d{1,9}(\.\d{1,9})?)$""", RegexOptions.IgnoreCase)

    let formatError =
        function
        | ParsedDecimal.BelowMin _ -> "Minimum number allowed is -999999999."
        | ParsedDecimal.AboveMax _ -> "Maximum number allowed is 999999999."
        | ParsedDecimal.Invalid str -> sprintf "\"%s\" is not valid." str
        | _ -> ""

    let toOption =
        function
        | ParsedDecimal.Valid d -> Some d
        | _ -> None

    let fromOption =
        function
        | None -> ParsedDecimal.Empty
        | Some x -> ParsedDecimal.Valid x

    let private matchDecimal input =
        let res = regex.Match(input)

        if not res.Success then
            ParsedDecimal.Invalid input
        else
            match System.Decimal.TryParse input with
            | true, x when x < minDec -> ParsedDecimal.BelowMin input
            | true, x when x > maxDec -> ParsedDecimal.AboveMax input
            | true, x -> ParsedDecimal.Valid x
            | false,_ -> ParsedDecimal.Invalid input

    let ofString str =
        if String.IsNullOrWhiteSpace str then 
            (ParsedDecimal.Empty, None)
        else
            match matchDecimal str with
            | ParsedDecimal.Empty as x -> x, None
            | ParsedDecimal.Valid _ as x -> x, None
            | ParsedDecimal.BelowMin _ as x -> x, formatError x |> Some 
            | ParsedDecimal.AboveMax _ as x -> x, formatError x |> Some 
            | ParsedDecimal.Invalid _ as x -> x, formatError x |> Some 

    let toString formatter = 
        function
        | ParsedDecimal.Empty -> ""
        | ParsedDecimal.Valid d -> formatter d
        | ParsedDecimal.Invalid str
        | ParsedDecimal.BelowMin str
        | ParsedDecimal.AboveMax str -> str

    let formatToOption formatter = 
        function
        | ParsedDecimal.Empty -> None
        | ParsedDecimal.Valid d -> Some <| formatter d
        | ParsedDecimal.Invalid str 
        | ParsedDecimal.BelowMin str
        | ParsedDecimal.AboveMax str -> Some str

type ParsedNumber =
        | Int of ParsedInt
        | Decimal of ParsedDecimal

type Number =
    | Int of int
    | Decimal of decimal
    with
        static member (+) (x, y) =
            match x, y with
            | Int i1, Int i2 -> Int (i1+i2)
            | Int i, Decimal d | Decimal d, Int i -> Int (i + int d)
            | Decimal d1, Decimal d2 -> Decimal (d1+d2)

        static member (-) (x, y) =
            match x, y with
            | Int i1, Int i2 -> Int (i1-i2)
            | Int i, Decimal d -> Int (i - int d)
            | Decimal d, Int i -> Int (int d - i)
            | Decimal d1, Decimal d2 -> Decimal (d1-d2)

module ParsedNumber =
    let apply fi fd =
        function
        | ParsedNumber.Int x -> fi x
        | ParsedNumber.Decimal x -> fd x

    let mapOpt fi fd =
        function
        | ParsedNumber.Int x -> ParsedInt.toOption x |> fi
        | ParsedNumber.Decimal x -> ParsedDecimal.toOption x |> fd
    
    let isNone = mapOpt Option.isNone Option.isNone
    let coorce = mapOpt (Option.defaultValue 0 >> Number.Int) (Option.defaultValue 0M >> Number.Decimal)
    let toNumber = mapOpt (Option.map Number.Int) (Option.map Number.Decimal)
    
    let ofNumber =
        function
        | Number.Int i -> ParsedNumber.Int (ParsedInt.Valid i)
        | Number.Decimal d -> ParsedNumber.Decimal (ParsedDecimal.Valid d)

    let format = apply (ParsedInt.toString (fun x -> x.ToString())) (ParsedDecimal.toString (fun x -> x.ToString()))

module Number =
    let apply f1 f2 =
        function
        | Number.Int x -> f1 x
        | Number.Decimal x -> f2 x

    let format = apply (fun x -> x.ToString()) (fun x -> x.ToString())
    let preventNegative = 
        apply 
            (fun x -> if x < 0 then true, Number.Int 0 else false, Number.Int x) 
            (fun x -> if x < 0M then true, Number.Decimal 0M else false, Number.Decimal x)

    let asInt = apply id (fun _ -> failwith "Expected int")
    let asDecimal = apply (fun _ -> failwith "Expected decimal") id